// ActionButtons.js
import { CircularProgress, Select, MenuItem, Switch } from '@mui/material'
import PaymentStatusNote from './PaymentStatusNote'

const buttonStyle = {
  backgroundColor: 'rgba(89, 104, 183, 0.5)',
  outline: 'none',
  border: 'none',
  boxShadow: '4px 4px 20px 1px rgba(255, 255, 255, 0.02)',
  borderRadius: '40px',
  // padding: '6px 8px',
  fontSize: '14px',
  minHeight: '28px',
  color: 'black',
  marginBottom: '8px',
}

const ActionButtons = ({
  onEmailTeam,
  onEmailCustomer,
  onSave, // New prop for save operation
  onDownload, // New prop for download operation
  onExportToZoho,
  processingButton, // New prop to track which button is processing
  zohoConnected,
  paymentStatus,
  dbPaymentStatus, // New prop for saved DB payment status
  onPaymentStatusChange,
  invoiceExists, // New prop to check if invoice exists
  sentDate, // New prop for sent date
  emailSent, // New prop for email sent status
  autoGenerate, // New prop for auto generate status
  onAutoGenerateChange, // New prop for auto generate change handler
}) => {
  const isAnyButtonProcessing = processingButton !== null

  const getBackgroundColor = (status) => {
    switch (status) {
      case 'PAID':
        return 'rgba(89, 104, 183, 0.5)'
      case 'PENDING':
      case 'UNPAID':
      default:
        return 'rgba(89, 104, 183, 0.5)'
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '20px',
      }}
    >
      {/* Payment Status Note on the right */}
      <PaymentStatusNote
        paymentStatus={paymentStatus}
        dbPaymentStatus={dbPaymentStatus}
        sentDate={sentDate}
        emailSent={emailSent}
      />
      {/* Action Buttons on the left */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'end',
          gap: '10px',
          flex: 1,
        }}
      >
        <Select
          value={(paymentStatus || 'UNPAID').toUpperCase()}
          onChange={(e) => onPaymentStatusChange(e.target.value)}
          disabled={isAnyButtonProcessing}
          sx={{
            ...buttonStyle,
            backgroundColor: getBackgroundColor(paymentStatus),
            minWidth: '140px',
            cursor: isAnyButtonProcessing ? 'not-allowed' : 'pointer',
            opacity: isAnyButtonProcessing ? 0.6 : 1,
          }}
        >
          <MenuItem value="PAID">Paid</MenuItem>
          <MenuItem value="UNPAID">Unpaid</MenuItem>
          <MenuItem value="PENDING">Pending</MenuItem>
        </Select>

        <button
          style={{
            ...buttonStyle,
            minWidth: '140px',
            cursor:
              (isAnyButtonProcessing && processingButton !== 'autoReminder') ||
              !invoiceExists
                ? 'not-allowed'
                : 'pointer',
            opacity:
              (isAnyButtonProcessing && processingButton !== 'autoReminder') ||
              !invoiceExists
                ? 0.6
                : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
          onClick={() => onAutoGenerateChange(!autoGenerate)}
          disabled={
            (isAnyButtonProcessing && processingButton !== 'autoReminder') ||
            !invoiceExists
          }
          title={!invoiceExists ? 'Please create invoice first' : ''}
        >
          <span>Auto Reminder</span>
          <Switch
            checked={Boolean(autoGenerate)}
            onChange={(e) => {
              e.stopPropagation()
              onAutoGenerateChange(e.target.checked)
            }}
            size="medium"
            disabled={
              (isAnyButtonProcessing && processingButton !== 'autoReminder') ||
              !invoiceExists
            }
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: '#fff',
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#4caf50',
              },
            }}
          />
        </button>

        <button
          style={{
            ...buttonStyle,
            minWidth: '150px',
            cursor:
              (isAnyButtonProcessing && processingButton !== 'emailTeam') ||
              !invoiceExists
                ? 'not-allowed'
                : 'pointer',
            opacity:
              (isAnyButtonProcessing && processingButton !== 'emailTeam') ||
              !invoiceExists
                ? 0.6
                : 1,
          }}
          onClick={onEmailTeam}
          disabled={
            (isAnyButtonProcessing && processingButton !== 'emailTeam') ||
            !invoiceExists
          }
          title={!invoiceExists ? 'Please create invoice first' : ''}
        >
          {processingButton === 'emailTeam' ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'row',
              }}
            >
              <div style={{ fontSize: '16px', marginRight: '5px' }}>
                Processing
              </div>
              <CircularProgress
                style={{ height: '12px', width: '12px', color: 'white' }}
                size={12}
              />
            </div>
          ) : (
            'Send Email to Team'
          )}
        </button>

        <button
          style={{
            ...buttonStyle,
            minWidth: '120px',
            cursor:
              isAnyButtonProcessing && processingButton !== 'save'
                ? 'not-allowed'
                : 'pointer',
            opacity:
              isAnyButtonProcessing && processingButton !== 'save' ? 0.6 : 1,
          }}
          onClick={onSave}
          disabled={isAnyButtonProcessing && processingButton !== 'save'}
        >
          {processingButton === 'save' ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'row',
              }}
            >
              <div style={{ fontSize: '14px', marginRight: '5px' }}>
                Processing
              </div>
              <CircularProgress
                style={{ height: '12px', width: '12px', color: 'white' }}
                size={12}
              />
            </div>
          ) : invoiceExists ? (
            'Update'
          ) : (
            'Save'
          )}
        </button>

        <button
          style={{
            ...buttonStyle,
            minWidth: '120px',
            cursor:
              isAnyButtonProcessing && processingButton !== 'download'
                ? 'not-allowed'
                : 'pointer',
            opacity:
              isAnyButtonProcessing && processingButton !== 'download'
                ? 0.6
                : 1,
          }}
          onClick={onDownload}
          disabled={isAnyButtonProcessing && processingButton !== 'download'}
        >
          {processingButton === 'download' ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'row',
              }}
            >
              <div style={{ fontSize: '14px', marginRight: '5px' }}>
                Processing
              </div>
              <CircularProgress
                style={{ height: '12px', width: '12px', color: 'white' }}
                size={12}
              />
            </div>
          ) : (
            'Download'
          )}
        </button>

        <button
          style={{
            ...buttonStyle,
            minWidth: '150px',
            cursor:
              (isAnyButtonProcessing && processingButton !== 'emailCustomer') ||
              !invoiceExists
                ? 'not-allowed'
                : 'pointer',
            opacity:
              (isAnyButtonProcessing && processingButton !== 'emailCustomer') ||
              !invoiceExists
                ? 0.6
                : 1,
          }}
          onClick={onEmailCustomer}
          disabled={
            (isAnyButtonProcessing && processingButton !== 'emailCustomer') ||
            !invoiceExists
          }
          title={!invoiceExists ? 'Please create invoice first' : ''}
        >
          {processingButton === 'emailCustomer' ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'row',
              }}
            >
              <div style={{ fontSize: '14px', marginRight: '5px' }}>
                Processing
              </div>
              <CircularProgress
                style={{ height: '12px', width: '12px', color: 'white' }}
                size={12}
              />
            </div>
          ) : (
            'Send Email to Customer'
          )}
        </button>

        <button
          style={{
            ...buttonStyle,
            backgroundColor: zohoConnected
              ? 'rgba(144, 238, 144, 0.8)'
              : buttonStyle.backgroundColor,
            minWidth: '140px',
            cursor:
              (isAnyButtonProcessing && processingButton !== 'exportZoho') ||
              zohoConnected ||
              !invoiceExists
                ? 'not-allowed'
                : 'pointer',
            opacity:
              (isAnyButtonProcessing && processingButton !== 'exportZoho') ||
              zohoConnected ||
              !invoiceExists
                ? 0.6
                : 1,
          }}
          onClick={onExportToZoho}
          disabled={
            (isAnyButtonProcessing && processingButton !== 'exportZoho') ||
            zohoConnected ||
            !invoiceExists
          }
          title={!invoiceExists ? 'Please create invoice first' : ''}
        >
          {processingButton === 'exportZoho' ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'row',
              }}
            >
              <div style={{ fontSize: '14px', marginRight: '5px' }}>
                Processing
              </div>
              <CircularProgress
                style={{ height: '12px', width: '12px', color: 'white' }}
                size={12}
              />
            </div>
          ) : zohoConnected ? (
            'Connected to Zoho'
          ) : (
            'Export to Zoho'
          )}
        </button>
      </div>
    </div>
  )
}

export default ActionButtons
